import { Posting } from '../services/apiService';

/**
 * Calculate the residual amount needed to balance a transaction.
 * Returns null if postings use mixed commodities (balance not applicable).
 * Returns the negated sum of all filled posting amounts for the single commodity case.
 */
export function calculateResidual(
  postings: Posting[],
  excludeIndex: number,
): { commodity: string; amount: number } | null {
  const filled = postings.filter((_, i) => i !== excludeIndex);
  if (filled.length === 0) return null;

  const commodities = new Set(postings.map((p) => p.commodity).filter(Boolean));
  if (commodities.size > 1) return null;

  const sum = filled.reduce((acc, p) => acc + (p.amount || 0), 0);
  return { commodity: postings[0].commodity || 'BRL', amount: -sum };
}

/**
 * Check if a transaction is balanced (sum of all postings ≈ 0).
 * Returns true if balanced, false if not.
 * Mixed commodity transactions always return false (cannot auto-check).
 */
export function isTransactionBalanced(postings: Posting[]): boolean {
  const commodities = new Set(postings.map((p) => p.commodity).filter(Boolean));
  if (commodities.size > 1) return false;

  const sum = postings.reduce((acc, p) => acc + (p.amount || 0), 0);
  return Math.abs(sum) < 0.005;
}

/**
 * Fuzzy match an account name against a query string.
 * Splits query by spaces and checks if all parts appear in the account (case-insensitive).
 * Returns a relevance score (higher = better match), or -1 for no match.
 */
export function fuzzyMatchAccount(account: string, query: string): number {
  const lower = account.toLowerCase();
  const parts = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 0;

  let score = 0;
  for (const part of parts) {
    const idx = lower.indexOf(part);
    if (idx === -1) return -1;
    // Prefer matches at segment boundaries (after : or start)
    if (idx === 0 || lower[idx - 1] === ':') {
      score += 2;
    } else {
      score += 1;
    }
  }
  // Shorter accounts rank higher (more specific match)
  score += 1 / account.length;
  return score;
}
